import type {BadgeProps} from "@/components/ui/badge";
import {
  resolveRowAmenityAccessRestricted,
  resolveRowAmenityAccessRestrictedAt,
  type AmenityRestrictionRowSource,
} from "@/src/modules/households/lib/household-amenity-restriction";
import {PERMIT_AMENITY_DISPLAY_SUSPEND_REASON} from "@/src/modules/permanent-parking/lib/permit-status";

import type {Safelisting} from "../types";
import type {SafelistingCoverageStatus} from "./has-future-date";
import {getSafelistingCoverageStatusBadgeProps, getSafelistingCoverageStatus, hasFutureDate} from "./has-future-date";

export type SafelistingParentLifecycleStatus = "active" | "suspended" | "cancelled" | "unknown";

type SafelistingStatusSource = Pick<Safelisting, "status" | "status_label">;

function normalizeStatusToken(status: Safelisting["status"]): string | null {
  if (status == null || status === "") return null;
  return String(status).trim().toLowerCase();
}

/** Parses API parent lifecycle from `status` or `status_label`. */
export function getSafelistingParentLifecycleStatus(
  source: SafelistingStatusSource,
): SafelistingParentLifecycleStatus {
  const label = source.status_label?.trim().toLowerCase();
  if (label === "suspended") return "suspended";
  if (label === "cancelled" || label === "revoked") return "cancelled";
  if (label === "active") return "active";

  const token = normalizeStatusToken(source.status);
  if (token === "2" || token === "suspended") return "suspended";
  if (token === "0" || token === "cancelled" || token === "revoked") return "cancelled";
  if (token === "1" || token === "active") return "active";
  return "unknown";
}

export function isSafelistingParentSuspended(source: SafelistingStatusSource): boolean {
  return getSafelistingParentLifecycleStatus(source) === "suspended";
}

export function isSafelistingParentCancelled(source: SafelistingStatusSource): boolean {
  return getSafelistingParentLifecycleStatus(source) === "cancelled";
}

export type SafelistingSuspendDisplaySource = AmenityRestrictionRowSource &
  SafelistingStatusSource & {
    suspend_reason?: string | null;
    suspended_at?: string | null;
  };

/** True when UI should show Suspended (API parent or household amenity overlay on live coverage). */
export function isSafelistingDisplaySuspended(
  source: SafelistingSuspendDisplaySource,
  coverageStatus: SafelistingCoverageStatus,
): boolean {
  if (isSafelistingParentSuspended(source)) return true;
  const amenityRestricted = resolveRowAmenityAccessRestricted(source);
  return amenityRestricted && (coverageStatus === "active" || coverageStatus === "scheduled");
}

export function getSafelistingSuspendReasonForDisplay(
  source: SafelistingSuspendDisplaySource | null | undefined,
  coverageStatus: SafelistingCoverageStatus,
): string | null | undefined {
  if (!source) return undefined;
  if (isSafelistingParentSuspended(source)) {
    return source.suspend_reason?.trim() || PERMIT_AMENITY_DISPLAY_SUSPEND_REASON;
  }
  if (isSafelistingDisplaySuspended(source, coverageStatus)) {
    return PERMIT_AMENITY_DISPLAY_SUSPEND_REASON;
  }
  return source.suspend_reason;
}

export function getSafelistingSuspendedAtForDisplay(
  source: SafelistingSuspendDisplaySource | null | undefined,
  coverageStatus: SafelistingCoverageStatus,
): string | null | undefined {
  if (!source) return undefined;
  if (isSafelistingParentSuspended(source)) {
    return source.suspended_at?.trim() || resolveRowAmenityAccessRestrictedAt(source);
  }
  if (isSafelistingDisplaySuspended(source, coverageStatus)) {
    return resolveRowAmenityAccessRestrictedAt(source);
  }
  return source.suspended_at;
}

/** Status badge — parent suspended wins; else coverage with optional amenity overlay. */
export function getSafelistingDisplayBadgeProps(
  source: SafelistingSuspendDisplaySource,
  coverageStatus: SafelistingCoverageStatus,
  amenityRestrictedOverride?: boolean,
): {label: string; variant: BadgeProps["variant"]} {
  if (isSafelistingParentSuspended(source)) {
    return {label: "Suspended", variant: "warning"};
  }

  const amenityRestricted =
    amenityRestrictedOverride ?? resolveRowAmenityAccessRestricted(source);
  if (amenityRestricted && (coverageStatus === "active" || coverageStatus === "scheduled")) {
    return {label: "Suspended", variant: "warning"};
  }

  return getSafelistingCoverageStatusBadgeProps(coverageStatus);
}

export type SafelistingsEmbedStatusSource = {
  active?: number | null;
  status?: number | string | null;
  status_label?: string | null;
  suspended?: number | null;
  suspended_count?: number | null;
};

function readSuspendedEmbedCount(meta: SafelistingsEmbedStatusSource): number {
  const raw = meta.suspended ?? meta.suspended_count ?? 0;
  return typeof raw === "number" && raw > 0 ? raw : 0;
}

/** No active or suspended safelisting on vehicle per GET vehicles embed counts. */
export function isSafelistingsEmbedCountVacant(
  meta: SafelistingsEmbedStatusSource | null | undefined,
): boolean {
  if (!meta) return false;
  return (meta.active ?? 0) <= 0 && readSuspendedEmbedCount(meta) === 0;
}

/** Maps GET vehicles `safelistings` embed to list/KPI badge when parent lifecycle is known. */
export function getSafelistingsEmbedStatusBadgeProps(
  meta: SafelistingsEmbedStatusSource | null | undefined,
): {label: string; variant: BadgeProps["variant"]} | null {
  if (!meta) return null;

  const fromLabel = getSafelistingStatusBadgePropsFromApiLabel(meta.status_label);
  if (fromLabel) return fromLabel;

  const lifecycle = getSafelistingParentLifecycleStatus({
    status: meta.status as Safelisting["status"],
    status_label: meta.status_label,
  });
  if (lifecycle === "suspended") {
    return {label: "Suspended", variant: "warning"};
  }
  if (lifecycle === "cancelled") {
    return {label: "Cancelled", variant: "error"};
  }

  if (readSuspendedEmbedCount(meta) > 0) {
    return {label: "Suspended", variant: "warning"};
  }

  return null;
}

/** True when any loaded safelisting row has API parent lifecycle suspended. */
export function hasSuspendedSafelistingInRows(
  safelistings: Pick<Safelisting, "status" | "status_label">[] | null | undefined,
): boolean {
  if (!safelistings?.length) return false;
  return safelistings.some((row) => isSafelistingParentSuspended(row));
}

/** Maps GET vehicles `safelistings.status_label` to list/KPI badge styling. */
export function getSafelistingStatusBadgePropsFromApiLabel(
  statusLabel: string | null | undefined,
): {label: string; variant: BadgeProps["variant"]} | null {
  const raw = statusLabel?.trim();
  if (!raw) return null;
  const key = raw.toLowerCase();
  if (key === "suspended") return {label: "Suspended", variant: "warning"};
  if (key === "active") return {label: "Active", variant: "success"};
  if (key === "cancelled" || key === "revoked") return {label: "Cancelled", variant: "error"};
  if (key === "scheduled") return {label: "Scheduled", variant: "info"};
  if (key === "expired") return {label: "Expired", variant: "warning"};
  return {label: raw, variant: "secondary"};
}

export function isSafelistingModificationBlocked(
  safelisting: Safelisting,
  timeZone?: string,
): boolean {
  return isSafelistingParentSuspended(safelisting);
}

export function canModifySafelisting(safelisting: Safelisting, timeZone?: string): boolean {
  return hasFutureDate(safelisting, timeZone) && !isSafelistingParentSuspended(safelisting);
}

export function getSafelistingModificationBlockedTitle(action: "edit" | "delete" | "cancel" | "correct"): string {
  switch (action) {
    case "edit":
      return "Cannot edit: safelisting is suspended";
    case "delete":
      return "Cannot delete: safelisting is suspended";
    case "cancel":
      return "Cannot cancel: safelisting is suspended";
    case "correct":
      return "Cannot correct accountability: safelisting is suspended";
  }
}
